/**
 * 商談詳細「概要」タブ。
 *  - 編集: title / memo / expectedAmount / primaryCategory
 *  - ステージ操作: 前後遷移ボタン (成約/失注確定は DealDetailPage ヘッダー右上に集約)
 *  - activities タイムライン
 */
import type { DealActivityType, DealStage } from '@goenhub/shared';
import {
  Activity,
  ArrowLeftCircle,
  ArrowRightCircle,
  CheckCircle,
  FileText,
  GitMerge,
  Mail,
  MessageSquare,
  Save,
  XCircle,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input, Label, Textarea } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import {
  useDealActivities,
  useTransitionDealStage,
  useUpdateDeal,
  type DealRecord,
} from '@/hooks/useDeals';
import { useProductCategories } from '@/hooks/useProductCategories';

const ACTIVITY_TYPE_LABEL: Record<DealActivityType, string> = {
  form_submitted: 'フォーム送信',
  mail_sent: 'メール送信',
  meeting_held: '商談・打合せ',
  merged_in: '統合（取込）',
  merged_out: '統合（移動）',
  split_off: '分割',
  stage_changed: 'ステージ変更',
  won_confirmed: '成約確定',
  lost_confirmed: '失注確定',
  memo: 'メモ',
  manual: '手動記録',
};

const ACTIVITY_ICON: Record<DealActivityType, typeof Activity> = {
  form_submitted: FileText,
  mail_sent: Mail,
  meeting_held: MessageSquare,
  merged_in: GitMerge,
  merged_out: GitMerge,
  split_off: GitMerge,
  stage_changed: Activity,
  won_confirmed: CheckCircle,
  lost_confirmed: XCircle,
  memo: Activity,
  manual: Activity,
};

/** 通常進行のステージ順 (won/lost 以外) */
const STAGE_PROGRESSION: DealStage[] = [
  'document_requested',
  'briefing',
  'quote',
  // ↓ won 経由でしか入らないが、UI 上は前後遷移で動かせるように
  'paid',
  'ordered',
  'delivered',
];

const STAGE_LABEL: Record<DealStage, string> = {
  document_requested: '資料請求',
  briefing: '個別説明',
  quote: '見積',
  won: '成約',
  agreed: '同意',
  paid: '入金',
  ordered: '発注',
  delivered: '納品',
  lost: '失注',
};

interface Props {
  deal: DealRecord;
}

export function DealOverviewTab({ deal }: Props) {
  const update = useUpdateDeal();
  const transitionStage = useTransitionDealStage();
  const activities = useDealActivities(deal.id);

  const { data: categories } = useProductCategories();

  const [memo, setMemo] = useState(deal.memo ?? '');
  const [expectedAmount, setExpectedAmount] = useState<string>(
    deal.expectedAmount != null ? String(deal.expectedAmount) : '',
  );
  const [primaryCategoryId, setPrimaryCategoryId] = useState<string>(deal.primaryCategoryId ?? '');
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setMemo(deal.memo ?? '');
    setExpectedAmount(deal.expectedAmount != null ? String(deal.expectedAmount) : '');
    setPrimaryCategoryId(deal.primaryCategoryId ?? '');
    setDirty(false);
  }, [deal]);

  const isClosed = deal.status === 'closed';

  const handleSave = async () => {
    const expected = expectedAmount.trim() ? Number(expectedAmount.replace(/[,¥\s]/g, '')) : null;
    if (expected !== null && (!Number.isFinite(expected) || expected < 0)) {
      toast.error('期待金額は0以上の数値で入力してください');
      return;
    }
    try {
      // title はヘッダーのインライン編集に一本化 (2026-07-29)。
      // ここで送るとタブ表示中の古い値でヘッダーの編集を巻き戻すため含めない。
      await update.mutateAsync({
        id: deal.id,
        memo,
        expectedAmount: expected,
        primaryCategoryId: primaryCategoryId || null,
      });
      toast.success('保存しました');
      setDirty(false);
    } catch (err) {
      toast.error(`保存失敗: ${(err as Error).message}`);
    }
  };

  const handleMoveStage = async (toStage: DealStage) => {
    try {
      await transitionStage.mutateAsync({ dealId: deal.id, toStage });
      toast.success(`ステージを「${STAGE_LABEL[toStage]}」に変更しました`);
    } catch (err) {
      toast.error(`変更失敗: ${(err as Error).message}`);
    }
  };

  // 前後の stage を導出
  const currentIdx = STAGE_PROGRESSION.indexOf(deal.stage);
  const prevStage = currentIdx > 0 ? STAGE_PROGRESSION[currentIdx - 1] : null;
  const nextStage =
    currentIdx >= 0 && currentIdx < STAGE_PROGRESSION.length - 1
      ? STAGE_PROGRESSION[currentIdx + 1]
      : null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-6">
      {/* 左: 編集フィールド */}
      <div className="space-y-4">
        {/* 基本属性 */}
        <Section title="基本属性">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">期待金額</Label>
              <Input
                type="text"
                inputMode="numeric"
                value={expectedAmount}
                onChange={(e) => {
                  setExpectedAmount(e.target.value);
                  setDirty(true);
                }}
                placeholder="例: 800000"
                disabled={isClosed}
              />
            </div>
            <div>
              <Label className="text-xs">主軸カテゴリ</Label>
              <select
                value={primaryCategoryId}
                onChange={(e) => {
                  setPrimaryCategoryId(e.target.value);
                  setDirty(true);
                }}
                disabled={isClosed}
                className="w-full px-3 py-2 text-xs rounded-sm border border-[var(--border)] bg-[var(--bg-input)]"
              >
                <option value="">
                  未設定
                  {deal.primaryCategory && !deal.primaryCategoryId
                    ? ` (旧データ: ${deal.primaryCategory})`
                    : ''}
                </option>
                {(categories ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <Label className="text-xs">メモ</Label>
            <Textarea
              value={memo}
              onChange={(e) => {
                setMemo(e.target.value);
                setDirty(true);
              }}
              rows={3}
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button variant="primary" onClick={handleSave} disabled={!dirty || update.isPending}>
              <Save className="size-3.5" />
              {update.isPending ? '保存中…' : dirty ? '保存' : '保存済'}
            </Button>
          </div>
        </Section>

        {/* 担当者（社内）・SA ⇄ 紹介者・親（所有者）は商談詳細ヘッダーに集約 (2026-10、DealRelationsBar) */}

        {/* ステージ操作 */}
        <Section title="ステージ操作">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-[var(--text-muted)]">現在:</span>
            <Badge variant="accent">{STAGE_LABEL[deal.stage]}</Badge>
            {isClosed && <Badge variant="default">closed</Badge>}
          </div>

          {!isClosed && (
            <div className="flex items-center gap-2 flex-wrap">
              {prevStage && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleMoveStage(prevStage)}
                  disabled={transitionStage.isPending}
                >
                  <ArrowLeftCircle className="size-3.5" />「{STAGE_LABEL[prevStage]}」に戻す
                </Button>
              )}
              {nextStage && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleMoveStage(nextStage)}
                  disabled={transitionStage.isPending}
                >
                  「{STAGE_LABEL[nextStage]}」に進める
                  <ArrowRightCircle className="size-3.5" />
                </Button>
              )}
            </div>
          )}

          {/* 成約/失注確定は DealDetailPage ヘッダー右上に集約 */}
        </Section>

        {/* メタデータ — 日時情報優先、ID 系は折り畳み */}
        <Section title="メタデータ">
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
            <Meta label="作成日" value={fmtDateTime(deal.openedAt)} />
            <Meta label="最終更新" value={fmtDateTime(deal.lastTouchedAt)} />
            {deal.closedAt && <Meta label="クローズ日" value={fmtDateTime(deal.closedAt)} />}
          </div>
          <details className="mt-2 group">
            <summary className="text-[10px] text-[var(--text-muted)] cursor-pointer hover:text-[var(--text-secondary)] select-none">
              ▸ 内部 ID を表示
            </summary>
            <div className="grid grid-cols-1 gap-y-1 mt-1.5 pl-3 text-[10px] text-[var(--text-muted)] font-mono">
              <div>商談 ID: {deal.id}</div>
              <div>顧客 ID: {deal.contactId}</div>
              {deal.assigneeUid && <div>担当 uid: {deal.assigneeUid}</div>}
            </div>
          </details>
        </Section>
      </div>

      {/* 右: タイムライン */}
      <div>
        <Section title="タイムライン (activities)">
          {activities.isLoading ? (
            <Spinner />
          ) : !activities.data || activities.data.length === 0 ? (
            <p className="text-xs text-[var(--text-muted)] italic">活動履歴がありません。</p>
          ) : (
            <div className="relative pl-7 max-h-[600px] overflow-y-auto pr-1">
              <div className="absolute left-3 top-2 bottom-2 w-px bg-[var(--border)]" />
              {activities.data.map((a) => {
                const Icon = ACTIVITY_ICON[a.type] ?? Activity;
                return (
                  <div key={a.id} className="relative pb-4">
                    <div className="absolute -left-5 top-0.5 size-5 rounded-full bg-[var(--bg-surface)] border border-[var(--border)] flex items-center justify-center">
                      <Icon className="size-3 text-[var(--text-secondary)]" />
                    </div>
                    <div className="text-[13px] text-[var(--text-primary)]">{a.detail}</div>
                    <div className="text-[10px] text-[var(--text-muted)] tabular-nums mt-0.5">
                      {fmtDateTime(a.createdAt)} · {ACTIVITY_TYPE_LABEL[a.type] ?? a.type}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Section>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// 共通
// ─────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-4 space-y-3">
      <h3 className="text-xs font-semibold text-[var(--text-primary)] uppercase tracking-wider">
        {title}
      </h3>
      {children}
    </div>
  );
}

function Meta({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <div className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider">{label}</div>
      <div
        className={`text-[var(--text-secondary)] truncate ${mono ? 'font-mono text-[11px]' : ''}`}
      >
        {value}
      </div>
    </div>
  );
}

function fmtDateTime(v: unknown): string {
  if (!v) return '—';
  if (typeof v === 'string') {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return '—';
    return formatDate(d);
  }
  if (typeof v === 'object' && v !== null && 'toDate' in v) {
    return formatDate((v as { toDate: () => Date }).toDate());
  }
  return '—';
}

function formatDate(d: Date): string {
  return d.toLocaleString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}
